import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Switch,
} from "@mui/material";
import ChildCare from "@mui/icons-material/ChildCare";
import Polyline from "@mui/icons-material/Polyline";
import FavoriteBorder from "@mui/icons-material/FavoriteBorder";
import apiService from "../../../service/Api";
import type { IThemeState } from "../../../types/theme";

export interface TimelineEvent {
  id?: number;
  year: string;
  date: string;
  title: string;
  location: string;
  description?: string;
  display_status: string;
  icon: string;
  color: string;
  deceased?: number;
  created_at?: string;
  updated_at?: string;
}

const Row = ({
  title,
  icon,
  checked,
  onToggle,
  reduxTheme,
}: {
  title: string;
  icon: ReactNode;
  checked: boolean;
  onToggle: () => void;
  reduxTheme: IThemeState;
}) => (
  <Paper sx={{ flex: 1, p: 2, height: 70, borderRadius: 2 }}>
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Box display="flex" alignItems="center" gap={1}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: reduxTheme.primary_colors?.[800],
          }}
        >
          {icon}
        </Box>
        <Typography variant="h6">{title}</Typography>
      </Box>
      <Switch
        checked={checked}
        onChange={() => onToggle()} // ✅ ensures callback always fires
      />
    </Box>
  </Paper>
);

const TimelinePanel = ({
  reduxTheme,
  deceasedId,
}: {
  reduxTheme: IThemeState;
  deceasedId: number | null;
}) => {
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[]>();
  const fetchTimelineEvents = useCallback(async () => {
    if (!deceasedId) return;
    try {
      const response = await apiService.get<TimelineEvent[]>(
        `/api/client-portal/timeline-events/?deceased_id=${deceasedId}`
      );
      const data = response.data;

      const sortedEvents = (data || []).sort(
        (a, b) => parseInt(a.year) - parseInt(b.year)
      );
      setTimelineEvents(sortedEvents);
    } catch (err: unknown) {
      console.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to fetch timeline events"
      );
    }
  }, [deceasedId]);

  useEffect(() => {
    if (deceasedId) fetchTimelineEvents();
  }, [deceasedId, fetchTimelineEvents]);

 const handleDisplayStatusChange = async (
  id: number | undefined,
  currentStatus: string
) => {
  if (!id) return;

  const newStatus = currentStatus === "public" ? "private" : "public";

  // Optimistically update the UI
  setTimelineEvents((prev) =>
    prev?.map((item) =>
      item.id === id ? { ...item, display_status: newStatus } : item
    )
  );

  try {
   
    await apiService.patch(`/api/individuals/deceased-timeline/${id}/`, {
      display_status: newStatus,
    });
    console.log(`Event ${id} updated to ${newStatus}`);
  } catch (err: unknown) {
    console.error("PATCH failed:", err);

    
    setTimelineEvents((prev) =>
      prev?.map((item) =>
        item.id === id ? { ...item, display_status: currentStatus } : item
      )
    );
  }
};

  const getRowProps = (icon: string) => {
    switch (icon) {
      case "home":
        return (
          <Polyline
            sx={{ color: reduxTheme.primary_colors?.[400], fontSize: 18 }}
          />
        );
      case "birth":
        return (
          <ChildCare
            sx={{ color: reduxTheme.primary_colors?.[400], fontSize: 18 }}
          />
        );
      case "education":
        return (
          <FavoriteBorder
            sx={{ color: reduxTheme.primary_colors?.[400], fontSize: 14 }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Card
      sx={{
        height: 482,
        backgroundColor: reduxTheme.primary_colors?.[800],
        borderRadius: 3,
        pt: 14,
        width: 660,
      }}
    >
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold", mt: -12 }}>
            Choose timeline options to display
          </Typography>

          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mt: -12 }}>
              Enable/Disable
            </Typography>
            <Switch size="small" sx={{ mt: -12 }} defaultChecked />
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" gap={2}>
          {timelineEvents && timelineEvents.length > 0 ? (
            timelineEvents.reduce((rows: React.ReactNode[], _, index) => {
              if (index % 2 === 0) {
                const first = timelineEvents[index];
                const second = timelineEvents[index + 1];

                rows.push(
                  <Box key={first.id} display="flex" gap={2}>
                    <Row
                      title={first.title}
                      icon={getRowProps(first.icon)}
                      checked={first.display_status === "public"}
                      onToggle={() =>
                        handleDisplayStatusChange(
                          first.id,
                          first.display_status
                        )
                      }
                      reduxTheme={reduxTheme}
                    />

                    {second ? (
                      <Row
                        title={second.title}
                        icon={getRowProps(second.icon)}
                        checked={second.display_status === "public"}
                        onToggle={() =>
                          handleDisplayStatusChange(
                            second.id,
                            second.display_status
                          )
                        }
                        reduxTheme={reduxTheme}
                      />
                    ) : (
                      <Box sx={{ flex: 1, minHeight: 70 }} />
                    )}
                  </Box>
                );
              }
              return rows;
            }, [])
          ) : (
            <Typography variant="h6" sx={{ fontWeight: "bold", mt: -12 }}>
              No timeline events to display
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default TimelinePanel;
